import React, { useState, useEffect, useRef } from 'react';
import Header from './Header'; // adjust path to where Header.jsx is
import Footer from './Footer'; // adjust path to where Footer.jsx is

// --- HELPER & UTILITY COMPONENTS (These would typically be in a shared components.jsx file) ---

const useOnScreen = (options) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            }
        }, options);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);
    
    return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
    return (
        <section ref={ref} id={id} className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {children}
        </section>
    );
};

const PageHeader = ({ title, subtitle }) => (
    <section className="relative py-24 bg-slate-50">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
        </div>
    </section>
);

// --- ICONS FOR THIS PAGE ---
const Search = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);
const Rocket = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.3.05-3.12-.65-.82-2.17-1.43-3.12-1.88z" /><path d="M12 15.5V13a2 2 0 0 0-2-2H8.5" /><path d="M16 10.5a5.5 5.5 0 0 0-5.5-5.5" /><path d="M12 2.5a2.5 2.5 0 0 1 2.5 2.5" /><path d="M19 13a2.5 2.5 0 0 1 2.5 2.5" /><path d="M19 19.5a5.5 5.5 0 0 0-5.5-5.5" /><path d="M21.5 4.5c-1.26 1.5-5 2-5 2s.5 3.74 2 5c.84.71 2.3.7 3.12.05.82-.65 1.43-2.17 1.88-3.12z" /></svg>
);
const CreditCard = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
);
const User = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);
const Link = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"></path></svg>
);

// --- MOCK DATA for Help Center ---
const helpCategories = [
    {
        id: 1,
        title: "Getting Started",
        icon: Rocket,
        description: "Learn the basics of setting up your account and launching your first campaign.",
        articles: [
            "How to connect your email account",
            "Creating your first outreach sequence",
            "Importing your contact list",
        ]
    },
    {
        id: 2,
        title: "Billing & Credits",
        icon: CreditCard,
        description: "Manage your payments, credits, and understand our pricing.",
        articles: [
            "How our credit system works",
            "Upgrading your credit package",
            "Viewing your payment history",
        ]
    },
    {
        id: 3,
        title: "Account Settings",
        icon: User,
        description: "Customize your profile, notifications, and other account preferences.",
        articles: [
            "Changing your password",
            "Setting up ",
            "Managing notification preferences",
        ]
    },
    {
        id: 4,
        title: "Integrations",
        icon: Link,
        description: "Connect Outreach with your favorite CRM and other tools.",
        articles: [
            "Connecting to Salesforce",
            "Using the Zapier integration",
            "API documentation for developers",
        ]
    }
];

const HelpCenterPage = () => (
    <>
      <Header />   {/* ✅ Added Header */}
      <main>
        <PageHeader 
            title="Help Center"
            subtitle="How can we help you today? Find answers to your questions below."
        />

        {/* Search Bar Section */}
        <AnimatedSection className="py-12" id="search-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative">
                    <input 
                        type="search"
                        placeholder="Search for articles..."
                        className="w-full pl-12 pr-4 py-4 text-lg bg-white border-2 border-slate-200 rounded-full shadow-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Search className="h-6 w-6 text-slate-400" />
                    </div>
                </div>
            </div>
        </AnimatedSection>
        
        {/* Categories Section */}
        <AnimatedSection className="py-24" id="categories-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid gap-8 md:grid-cols-2">
                    {helpCategories.map(category => {
                        const Icon = category.icon;
                        return (
                        <div key={category.id} className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                            <div className="flex items-center gap-4">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg">
                                    <Icon className="h-6 w-6"/>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-slate-900">{category.title}</h2>
                                    <p className="text-slate-500">{category.description}</p>
                                </div>
                            </div>
                            <ul className="mt-6 space-y-3 border-t pt-6">
                                {category.articles.map(article => (
                                    <li key={article}>
                                        <a href="#" className="flex justify-between items-center text-slate-700 hover:text-blue-600 hover:underline">
                                            <span>{article}</span>
                                            <span>&rarr;</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )})}
                </div>
            </div>
        </AnimatedSection>

        {/* Still Need Help? CTA */}
        <AnimatedSection className="py-24 bg-slate-800" id="contact-cta">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl font-extrabold text-white">Still Need Help?</h2>
                <p className="mt-4 text-lg text-slate-300">
                    If you can't find the answer you're looking for, don't hesitate to reach out to our support team.
                </p>
                <button className="mt-8 bg-white text-blue-600 font-bold py-3 px-8 rounded-full shadow-lg transition-transform duration-300 transform hover:scale-105">
                    Contact Support
                </button>
            </div>
        </AnimatedSection>
      </main>
      <Footer />   {/* ✅ Added Footer */}
    </>
);

export default HelpCenterPage;
